import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Button, VStack, Text, useDisclosure, useOutsideClick, Icon } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

export interface ClasswizeSelectOption<T = string> {
	value: T;
	label: string;
}

interface Props<T = string> {
	options: ClasswizeSelectOption<T>[];
	value?: T;
	onChange?: (value: T) => void;
	placeholder?: string;
	width?: string | number;
	isDisabled?: boolean;
	dataTestId?: string;
}

export function ClasswizeSelect<T = string>({
	options,
	value,
	onChange,
	placeholder = "Select",
	width = "200px",
	isDisabled = false,
	dataTestId: dataTestId,
}: Props<T>): JSX.Element {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selected, setSelected] = useState<T | undefined>(value);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setSelected(value);
	}, [value]);

	useOutsideClick({
		ref,
		handler: onClose,
	});

	const handleButtonClick = useCallback(() => {
		if (isDisabled) return;

		if (isOpen) {
			onClose();
		} else {
			onOpen();
		}
	}, [isOpen, onOpen, onClose, isDisabled]);

	const handleOptionClick = useCallback(
		(optionValue: T) => {
			setSelected(optionValue);
			onChange?.(optionValue);
			onClose();
		},
		[onChange, onClose]
	);

	const selectedOption = options.find((option) => option.value === selected);
	const displayText = selectedOption ? selectedOption.label : placeholder;

	return (
		<Box position="relative" ref={ref} width={width} data-testid={dataTestId}>
			<Button
				width="100%"
				justifyContent="space-between"
				onClick={handleButtonClick}
				variant="outline"
				border="1px solid"
				borderColor="neutrals.50"
				_focus={{ boxShadow: "outline", borderColor: "blue.500" }}
				isDisabled={isDisabled}
				opacity={isDisabled ? 0.6 : 1}
				background={"white"}
			>
				<Text color={selected ? "black" : "gray.500"} fontWeight={400}>
					{displayText}
				</Text>
				<Icon as={ChevronDownIcon} color={"gray.500"} boxSize={6} />
			</Button>

			{isOpen && (
				<VStack
					spacing={1}
					mt={2}
					p={2}
					border="1px solid"
					borderColor="gray.200"
					borderRadius="md"
					boxShadow="md"
					bg="white"
					zIndex={10}
					align="stretch"
					overflow="hidden"
					position="absolute"
					top="100%"
					left={0}
					right={0}
				>
					{options.map((option) => (
						<Box
							key={String(option.value)}
							px={3}
							py={2}
							rounded={"md"}
							_hover={{ bg: "gray.100", cursor: "pointer" }}
							bg={selected === option.value ? "#E7F9FF" : "white"}
							color="navy.900"
							onClick={() => handleOptionClick(option.value)}
							data-testid={`select-option-${String(option.value)}`}
						>
							{option.label}
						</Box>
					))}
				</VStack>
			)}
		</Box>
	);
}
